import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Box,
  MapPin,
  Wrench,
  ShieldAlert,
  Tag,
  Clock,
  Edit,
  ExternalLink,
  Boxes,
  Cloud,
  History,
  FolderTree,
  Trash2,
  AlertCircle,
  Radio,
  Cpu,
  Plus,
  Radar,
} from "lucide-react";
import { DMSDocumentsPanel } from "../components/dms/DMSDocumentsPanel";
import { AssetRadarGraph } from "../components/asset/AssetRadarGraph";
import { listDocuments, type DMSDocument } from "../lib/dmsApi";
import { Button } from "../components/ui/Button";
import { Card, CardHeader, CardTitle } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { StatusBadge } from "../components/ui/StatusBadge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import api from "../lib/api";
import {
  type Device as InstrumentationDevice,
  type Sensor as InstrumentationSensor,
  type SensorTag,
  listDevices as listInstrumentationDevices,
  listSensors as listInstrumentationSensors,
  listSensorTags,
} from "../lib/instrumentationApi";
import { useAppStore } from "../stores/appStore";
import { formatDate, formatDateTime } from "../lib/utils";

interface FunctionalLocationSummary {
  id: string;
  code: string;
  name: string;
  level: number;
  description?: string;
}

interface AssetDetail {
  id: string;
  virtual_site_id: string;
  name: string;
  asset_code: string;
  description?: string;
  status: string;
  criticality?: string;
  asset_type?: string;
  system?: string;
  sub_system?: string;
  functional_location_id?: string;
  functional_location?: FunctionalLocationSummary | null;
  template_id?: string;
  attributes?: Record<string, unknown>;
  metadata_?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface FlocOption {
  id: string;
  code: string;
  name: string;
  level: number;
  parent_id?: string | null;
  description?: string | null;
}

const ASSET_TYPES = [
  "pump", "valve", "compressor", "heat_exchanger", "tank", "pipe",
  "instrument", "electrical_panel", "motor", "conveyor", "hvac_unit",
  "structural", "other",
];
const ASSET_SYSTEMS = ["mechanical", "electrical", "instrumentation", "civil", "hvac"];
const ASSET_STATUSES = [
  "operational", "warning", "critical", "offline", "under_maintenance", "unknown",
];

interface TagItem {
  id: string;
  label: string | null;
  tag_type: string;
  geometry: Record<string, unknown>;
  color: string | null;
  point_cloud_id: string | null;
  digital_twin_id: string | null;
  created_at: string;
}

interface MaintenanceTask {
  id: string;
  title: string;
  status: string;
  priority?: string;
  due_date?: string;
  task_type?: string;
  created_at: string;
}

interface RiskAssessment {
  id: string;
  risk_score?: number;
  likelihood?: number;
  consequence?: number;
  status: string;
  assessed_at: string;
}

interface HistoryEntry {
  id: string;
  asset_id: string;
  action: string;
  changed_by?: string;
  changed_by_email?: string;
  changed_by_name?: string;
  changed_at: string;
  changes?: Record<string, unknown>;
  description?: string;
}

type TabType =
  | "details"
  | "tags"
  | "maintenance"
  | "risk"
  | "history"
  | "dms"
  | "instrumentation"
  | "radar";

function formatChangeValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedOrgId } = useAppStore();
  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceTask[]>([]);
  const [riskAssessments, setRiskAssessments] = useState<RiskAssessment[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  // Linked instrumentation devices + sensors for the new Instrumentation
  // tab. Refetched on tab open + after edits in this page.
  const [linkedDevices, setLinkedDevices] = useState<InstrumentationDevice[]>([]);
  const [linkedSensors, setLinkedSensors] = useState<InstrumentationSensor[]>([]);
  // sensor_id → first sensor 3D tag (point cloud or digital twin) so we can
  // surface a "View 3D Tag" deep link per sensor row. Only the first tag
  // is used because a sensor pinned in more than one model is rare and the
  // row only has space for one action button.
  const [sensorTagBySensor, setSensorTagBySensor] = useState<Map<string, SensorTag>>(new Map());
  // Documents linked to this asset — only fetched once the Asset Radar tab is
  // opened, since the Details / Documents tabs don't need this list.
  const [linkedDocuments, setLinkedDocuments] = useState<DMSDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>("details");

  // Inline-edit state. Toggled by the page-header Edit button; the Details
  // tab swaps its read-only cards for form fields, and the header buttons
  // become Save / Cancel. There is no separate modal — the user requested
  // editing in-place inside the existing Asset Information and Functional
  // Location containers.
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    asset_code: "",
    description: "",
    asset_type: "",
    system: "",
    sub_system: "",
    status: "",
    functional_location_id: "",
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [flocOptions, setFlocOptions] = useState<FlocOption[]>([]);

  // Inline functional-location create / edit. Lives inside the Functional
  // Location card on the Details tab. flMode controls whether the sub-form
  // is visible at all and which API call it'll fire on Save.
  const [flMode, setFlMode] = useState<"idle" | "create" | "edit">("idle");
  const [flForm, setFlForm] = useState({
    code: "",
    name: "",
    description: "",
    parent_id: "",
  });
  const [flError, setFlError] = useState<string | null>(null);
  const [isSavingFl, setIsSavingFl] = useState(false);

  // Asset delete state
  const [showDeleteAsset, setShowDeleteAsset] = useState(false);
  const [isDeletingAsset, setIsDeletingAsset] = useState(false);
  const [deleteAssetError, setDeleteAssetError] = useState<string | null>(null);

  const fetchInstrumentation = useCallback(async () => {
    if (!id || !asset?.virtual_site_id) return;
    try {
      const [devicesRes, sensorsRes] = await Promise.all([
        listInstrumentationDevices({
          virtual_site_id: asset.virtual_site_id,
          asset_id: id,
          page_size: 200,
        }),
        listInstrumentationSensors({ asset_id: id, page_size: 200 }),
      ]);
      setLinkedDevices(devicesRes.items);
      setLinkedSensors(sensorsRes.items);
      // Per-sensor 3D-tag lookup. Listed in parallel because the dedicated
      // /sensor-tags endpoint only filters by a single sensor_id; the count
      // of linked sensors per asset is small in practice so the fan-out is
      // cheap. Failures are isolated per call so one missing tag doesn't
      // hide the others.
      const tagMap = new Map<string, SensorTag>();
      if (sensorsRes.items.length > 0) {
        const tagResults = await Promise.all(
          sensorsRes.items.map((s) =>
            listSensorTags({ sensor_id: s.id }).catch(() => [] as SensorTag[]),
          ),
        );
        sensorsRes.items.forEach((s, idx) => {
          const tags = tagResults[idx];
          if (tags.length > 0) tagMap.set(s.id, tags[0]);
        });
      }
      setSensorTagBySensor(tagMap);
    } catch {
      setLinkedDevices([]);
      setLinkedSensors([]);
      setSensorTagBySensor(new Map());
    }
  }, [id, asset?.virtual_site_id]);

  // The Asset Radar tab depends on every related dataset, so the
  // Instrumentation + Documents queries are also fired when it's active.
  const fetchLinkedDocuments = useCallback(async () => {
    if (!id || !asset?.virtual_site_id) return;
    try {
      const res = await listDocuments({
        virtual_site_id: asset.virtual_site_id,
        linked_entity_type: "asset",
        linked_entity_id: id,
        limit: 200,
      });
      setLinkedDocuments(res.items);
    } catch {
      setLinkedDocuments([]);
    }
  }, [id, asset?.virtual_site_id]);

  useEffect(() => {
    if (activeTab === "instrumentation" || activeTab === "radar") {
      fetchInstrumentation();
    }
    if (activeTab === "radar") {
      fetchLinkedDocuments();
    }
  }, [activeTab, fetchInstrumentation, fetchLinkedDocuments]);

  useEffect(() => {
    async function load() {
      if (!selectedOrgId || !id) return;
      setIsLoading(true);
      try {
        const [assetRes, tagsRes, maintenanceRes, riskRes, historyRes] =
          await Promise.allSettled([
            api.get(`/assets/${id}`),
            api.get(`/tags`, { params: { asset_id: id } }),
            api.get(`/maintenance/tasks`, { params: { asset_id: id } }),
            api.get(`/risk/assessments`, { params: { asset_id: id } }),
            api.get(`/assets/${id}/history`),
          ]);

        if (assetRes.status === "fulfilled") setAsset(assetRes.value.data);
        if (tagsRes.status === "fulfilled")
          setTags(tagsRes.value.data?.items || tagsRes.value.data || []);
        if (maintenanceRes.status === "fulfilled")
          setMaintenance(maintenanceRes.value.data?.items || []);
        if (riskRes.status === "fulfilled")
          setRiskAssessments(riskRes.value.data?.items || []);
        if (historyRes.status === "fulfilled") setHistory(historyRes.value.data || []);
      } catch {
        /* ignore */
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [selectedOrgId, id]);

  async function enterEditMode() {
    if (!asset) return;
    // Snapshot the current asset into the form so Cancel can simply set
    // isEditing=false without restoring state explicitly.
    setEditForm({
      name: asset.name || "",
      asset_code: asset.asset_code || "",
      description: asset.description || "",
      asset_type: asset.asset_type || "",
      system: asset.system || "",
      sub_system: asset.sub_system || "",
      status: asset.status || "",
      functional_location_id: asset.functional_location_id || "",
    });
    setEditError(null);
    setActiveTab("details");
    setIsEditing(true);
    setFlMode("idle");
    setFlError(null);
    await refreshFlocOptions(asset.virtual_site_id);
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditError(null);
    setFlMode("idle");
    setFlError(null);
  }

  // Re-fetch the FL list for the current site. Used after creating or
  // editing an FL inline so the dropdown stays current without a full page
  // reload.
  async function refreshFlocOptions(virtualSiteId: string) {
    try {
      const res = await api.get("/functional-locations", {
        params: { virtual_site_id: virtualSiteId, page_size: 500 },
      });
      const items: FlocOption[] = (res.data.items || []).map((f: FlocOption) => ({
        id: f.id, code: f.code, name: f.name, level: f.level,
        parent_id: f.parent_id ?? null,
        description: f.description ?? null,
      }));
      items.sort((a, b) => a.level - b.level || a.code.localeCompare(b.code));
      setFlocOptions(items);
      return items;
    } catch {
      setFlocOptions([]);
      return [];
    }
  }

  function openFlCreate() {
    // Pre-fill the parent dropdown with the asset's current FL so the new
    // child naturally sits one level deeper. The user can change it.
    setFlForm({
      code: "",
      name: "",
      description: "",
      parent_id: editForm.functional_location_id || "",
    });
    setFlError(null);
    setFlMode("create");
  }

  function openFlEdit() {
    const fl = flocOptions.find((f) => f.id === editForm.functional_location_id);
    if (!fl) {
      setFlError("Pick a functional location to edit first.");
      return;
    }
    setFlForm({
      code: fl.code,
      name: fl.name,
      description: fl.description ?? "",
      parent_id: fl.parent_id ?? "",
    });
    setFlError(null);
    setFlMode("edit");
  }

  function closeFlForm() {
    setFlMode("idle");
    setFlError(null);
  }

  async function handleSaveFl() {
    if (!asset) return;
    if (!flForm.code.trim() || !flForm.name.trim()) {
      setFlError("Code and name are required.");
      return;
    }
    setIsSavingFl(true);
    setFlError(null);
    try {
      const params: Record<string, string> = {
        code: flForm.code.trim(),
        name: flForm.name.trim(),
      };
      if (flForm.description.trim()) params.description = flForm.description.trim();
      if (flForm.parent_id) params.parent_id = flForm.parent_id;
      let savedId: string;
      if (flMode === "create") {
        const res = await api.post("/functional-locations", null, {
          params: { ...params, virtual_site_id: asset.virtual_site_id },
        });
        savedId = res.data.id;
      } else {
        // Editing the currently-selected FL.
        const editingId = editForm.functional_location_id;
        if (!editingId) {
          setFlError("No FL selected to edit.");
          setIsSavingFl(false);
          return;
        }
        // The backend's PUT skips fields with empty/None values, so we always
        // send code+name; parent_id has to be explicit if it changed. We send
        // an empty string for parent_id when the user picked "(none)" so we
        // can detect it server-side… but the backend currently treats None as
        // "leave alone", so making something a root FL via this form is not
        // supported here. Tell the user.
        if (flForm.parent_id === "") {
          const original = flocOptions.find((f) => f.id === editingId);
          if (original?.parent_id) {
            setFlError(
              "Promoting an FL back to top-level isn't supported here. " +
              "Pick a different parent, or use the Functional Locations page.",
            );
            setIsSavingFl(false);
            return;
          }
        }
        const res = await api.put(
          `/functional-locations/${editingId}`, null, { params },
        );
        savedId = res.data.id;
      }
      const fresh = await refreshFlocOptions(asset.virtual_site_id);
      // Auto-select the FL we just created/edited so the asset shows it.
      if (fresh.some((f) => f.id === savedId)) {
        setEditForm({ ...editForm, functional_location_id: savedId });
      }
      setFlMode("idle");
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setFlError(error.response?.data?.detail || "Failed to save functional location.");
    } finally {
      setIsSavingFl(false);
    }
  }

  async function handleSaveEdit() {
    if (!asset) return;
    if (!editForm.name.trim()) {
      setEditError("Name is required");
      return;
    }
    if (!editForm.asset_code.trim()) {
      setEditError("Asset code is required");
      return;
    }
    setIsSavingEdit(true);
    setEditError(null);
    try {
      // Backend uses Query(None) and skips None params, so empty strings will
      // clear text fields. Enum/UUID params are only sent when non-empty.
      const params: Record<string, string> = {
        name: editForm.name.trim(),
        asset_code: editForm.asset_code.trim(),
        description: editForm.description,
        sub_system: editForm.sub_system,
      };
      if (editForm.asset_type) params.asset_type = editForm.asset_type;
      if (editForm.system) params.system = editForm.system;
      if (editForm.status) params.status = editForm.status;
      if (editForm.functional_location_id) {
        params.functional_location_id = editForm.functional_location_id;
      }
      await api.put(`/assets/${asset.id}`, null, { params });
      // The PUT endpoint returns AssetResponse (lean), not AssetDetailResponse,
      // which means the embedded functional_location summary is missing —
      // setAsset(res.data) would wipe it from the read-only display even
      // though the FK is still set. Re-fetch the full detail to keep the
      // Functional Location card populated after save.
      const [detail, hist] = await Promise.all([
        api.get(`/assets/${asset.id}`),
        api.get(`/assets/${asset.id}/history`).catch(() => null),
      ]);
      setAsset(detail.data);
      if (hist) setHistory(hist.data || []);
      setIsEditing(false);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setEditError(error.response?.data?.detail || "Failed to update asset");
    } finally {
      setIsSavingEdit(false);
    }
  }

  async function handleDeleteAsset() {
    if (!asset) return;
    setIsDeletingAsset(true);
    setDeleteAssetError(null);
    try {
      await api.delete(`/assets/${asset.id}`);
      navigate("/assets");
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setDeleteAssetError(error.response?.data?.detail || "Failed to delete asset");
    } finally {
      setIsDeletingAsset(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner size="lg" label="Loading asset..." />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <Box className="h-12 w-12 text-surface-600" />
        <p className="text-surface-400">Asset not found</p>
        <Button variant="secondary" onClick={() => navigate("/assets")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Assets
        </Button>
      </div>
    );
  }

  const tabs: { key: TabType; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: "details", label: "Details", icon: <Box className="h-4 w-4" /> },
    { key: "radar", label: "Asset Radar", icon: <Radar className="h-4 w-4" /> },
    { key: "dms", label: "Documents", icon: <FolderTree className="h-4 w-4" /> },
    { key: "tags", label: "3D Tags", icon: <Tag className="h-4 w-4" />, count: tags.length },
    {
      key: "instrumentation",
      label: "Instrumentation",
      icon: <Radio className="h-4 w-4" />,
      count: linkedDevices.length + linkedSensors.length,
    },
    { key: "maintenance", label: "Maintenance", icon: <Wrench className="h-4 w-4" />, count: maintenance.length },
    { key: "risk", label: "Risk", icon: <ShieldAlert className="h-4 w-4" />, count: riskAssessments.length },
    { key: "history", label: "History", icon: <History className="h-4 w-4" />, count: history.length },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/assets")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-surface-100">{asset.name}</h1>
              <StatusBadge status={asset.status} />
            </div>
            <p className="text-sm text-surface-400 mt-1">
              {asset.asset_code}
              {asset.description && ` - ${asset.description}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button
                variant="secondary"
                onClick={cancelEdit}
                disabled={isSavingEdit}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} isLoading={isSavingEdit}>
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={enterEditMode}>
                <Edit className="h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="danger"
                onClick={() => { setDeleteAssetError(null); setShowDeleteAsset(true); }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-700/50 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab.key
                ? "border-brand-500 text-brand-300"
                : "border-transparent text-surface-400 hover:text-surface-200"
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <Badge variant="brand" className="ml-1">{tab.count}</Badge>
            )}
          </button>
        ))}
      </div>

      {/* ── Details ───────────────────────────────────────────────── */}
      {activeTab === "details" && (
        <div className="space-y-4">
          {isEditing && editError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{editError}</span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Asset Information</CardTitle>
              </CardHeader>
              {isEditing ? (
                <div className="space-y-3">
                  <FieldRow label="Name" required>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      disabled={isSavingEdit}
                    />
                  </FieldRow>
                  <FieldRow label="Code" required>
                    <Input
                      value={editForm.asset_code}
                      onChange={(e) => setEditForm({ ...editForm, asset_code: e.target.value })}
                      disabled={isSavingEdit}
                    />
                  </FieldRow>
                  <FieldRow label="Status">
                    <SelectInput
                      value={editForm.status}
                      onChange={(v) => setEditForm({ ...editForm, status: v })}
                      options={ASSET_STATUSES}
                      disabled={isSavingEdit}
                    />
                  </FieldRow>
                  <FieldRow label="Type">
                    <SelectInput
                      value={editForm.asset_type}
                      onChange={(v) => setEditForm({ ...editForm, asset_type: v })}
                      options={ASSET_TYPES}
                      disabled={isSavingEdit}
                    />
                  </FieldRow>
                  <FieldRow label="System">
                    <SelectInput
                      value={editForm.system}
                      onChange={(v) => setEditForm({ ...editForm, system: v })}
                      options={ASSET_SYSTEMS}
                      disabled={isSavingEdit}
                    />
                  </FieldRow>
                  <FieldRow label="Sub-system">
                    <Input
                      value={editForm.sub_system}
                      onChange={(e) => setEditForm({ ...editForm, sub_system: e.target.value })}
                      disabled={isSavingEdit}
                    />
                  </FieldRow>
                  <FieldRow label="Description">
                    <Input
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      disabled={isSavingEdit}
                    />
                  </FieldRow>
                </div>
              ) : (
                <dl className="space-y-4">
                  {[
                    { label: "Name", value: asset.name },
                    { label: "Code", value: asset.asset_code },
                    { label: "Status", value: <StatusBadge status={asset.status} /> },
                    { label: "Type", value: asset.asset_type ? <span className="capitalize">{asset.asset_type.replace(/_/g, " ")}</span> : "-" },
                    { label: "System", value: asset.system ? <span className="capitalize">{asset.system}</span> : "-" },
                    { label: "Sub-system", value: asset.sub_system || "-" },
                    {
                      label: "Criticality",
                      value: asset.criticality ? <StatusBadge status={asset.criticality} /> : "-",
                    },
                    { label: "Description", value: asset.description || "-" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start justify-between">
                      <dt className="text-sm text-surface-400">{item.label}</dt>
                      <dd className="text-sm text-surface-200 text-right max-w-xs">
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-brand-400" />
                    Functional Location
                  </span>
                </CardTitle>
              </CardHeader>
              {isEditing ? (
                <div className="space-y-3">
                  <FieldRow label="Functional Location">
                    <div className="flex items-center gap-2">
                      <select
                        value={editForm.functional_location_id}
                        onChange={(e) =>
                          setEditForm({ ...editForm, functional_location_id: e.target.value })
                        }
                        disabled={isSavingEdit || flMode !== "idle"}
                        className="flex-1 rounded-lg border border-surface-600 bg-surface-800/50 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                      >
                        <option value="">— No specific FL (General Assets) —</option>
                        {flocOptions.map((f) => (
                          <option key={f.id} value={f.id}>
                            {"  ".repeat(f.level)}L{f.level} · {f.code} — {f.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        title="Edit selected FL (rename, change parent / level)"
                        onClick={openFlEdit}
                        disabled={isSavingEdit || flMode !== "idle" || !editForm.functional_location_id}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        title="Create a new functional location"
                        onClick={openFlCreate}
                        disabled={isSavingEdit || flMode !== "idle"}
                      >
                        <Plus className="h-3.5 w-3.5" /> New
                      </Button>
                    </div>
                  </FieldRow>
                  <p className="text-xs text-surface-500">
                    Each FL has a level (depth in the tree); creating one under a parent
                    auto-sets its level. Selecting "No specific FL" routes the asset to the
                    per-site General Assets bucket.
                  </p>

                  {flMode !== "idle" && (
                    <div className="rounded-lg border border-brand-500/30 bg-brand-500/5 p-3 space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-brand-200">
                        {flMode === "create" ? "New functional location" : "Edit functional location"}
                      </div>
                      <FieldRow label="Code" required>
                        <Input
                          value={flForm.code}
                          onChange={(e) => setFlForm({ ...flForm, code: e.target.value })}
                          placeholder="e.g. PLT-01"
                          disabled={isSavingFl}
                        />
                      </FieldRow>
                      <FieldRow label="Name" required>
                        <Input
                          value={flForm.name}
                          onChange={(e) => setFlForm({ ...flForm, name: e.target.value })}
                          placeholder="e.g. Plant 1"
                          disabled={isSavingFl}
                        />
                      </FieldRow>
                      <FieldRow label="Description">
                        <Input
                          value={flForm.description}
                          onChange={(e) =>
                            setFlForm({ ...flForm, description: e.target.value })
                          }
                          disabled={isSavingFl}
                        />
                      </FieldRow>
                      <FieldRow label="Parent FL (determines level)">
                        <select
                          value={flForm.parent_id}
                          onChange={(e) =>
                            setFlForm({ ...flForm, parent_id: e.target.value })
                          }
                          disabled={isSavingFl}
                          className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                        >
                          <option value="">— None (top-level / level 0) —</option>
                          {flocOptions
                            // When editing, prevent picking self as parent — the
                            // backend rejects this anyway but it's a nicer UX
                            // to filter it out here.
                            .filter(
                              (f) =>
                                flMode !== "edit" ||
                                f.id !== editForm.functional_location_id,
                            )
                            .map((f) => (
                              <option key={f.id} value={f.id}>
                                {"  ".repeat(f.level)}L{f.level} · {f.code} — {f.name}
                              </option>
                            ))}
                        </select>
                      </FieldRow>
                      {flError && (
                        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                          {flError}
                        </div>
                      )}
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={closeFlForm}
                          disabled={isSavingFl}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveFl}
                          isLoading={isSavingFl}
                        >
                          {flMode === "create" ? "Create FL" : "Save FL"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : asset.functional_location ? (
                <dl className="space-y-4">
                  <div className="flex items-start justify-between">
                    <dt className="text-sm text-surface-400">Name</dt>
                    <dd className="text-sm text-surface-200 font-medium">{asset.functional_location.name}</dd>
                  </div>
                  <div className="flex items-start justify-between">
                    <dt className="text-sm text-surface-400">Code</dt>
                    <dd className="text-sm font-mono text-brand-300">{asset.functional_location.code}</dd>
                  </div>
                  <div className="flex items-start justify-between">
                    <dt className="text-sm text-surface-400">Level</dt>
                    <dd className="text-sm text-surface-200">{asset.functional_location.level}</dd>
                  </div>
                  {asset.functional_location.description && (
                    <div className="flex items-start justify-between">
                      <dt className="text-sm text-surface-400">Description</dt>
                      <dd className="text-sm text-surface-200 text-right max-w-xs">
                        {asset.functional_location.description}
                      </dd>
                    </div>
                  )}
                  {asset.functional_location.code === "GENERAL" && (
                    <div className="rounded-lg border border-surface-700/50 bg-surface-800/40 px-3 py-2 text-xs text-surface-400">
                      This asset isn't linked to a specific FL — it lives in the per-site
                      catch-all "General Assets" bucket.
                    </div>
                  )}
                </dl>
              ) : (
                <div className="py-8 text-center text-sm text-surface-500">
                  No functional location assigned.
                </div>
              )}
              {!isEditing && asset.template_id && (
                <div className="mt-6 pt-4 border-t border-surface-700/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-surface-400">Template</span>
                    <span className="text-sm text-surface-200 font-mono">{asset.template_id}</span>
                  </div>
                </div>
              )}
              {!isEditing && asset.metadata_ && Object.keys(asset.metadata_).length > 0 && (
                <div className="mt-6 pt-4 border-t border-surface-700/50">
                  <h4 className="text-sm font-medium text-surface-300 mb-3">Custom Attributes</h4>
                  <dl className="space-y-2">
                    {Object.entries(asset.metadata_).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <dt className="text-xs text-surface-400 capitalize">{key.replace(/_/g, " ")}</dt>
                        <dd className="text-xs text-surface-200">{String(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* ── Asset Radar — network graph of every related entity ───── */}
      {activeTab === "radar" && (
        <AssetRadarGraph
          asset={asset}
          tags={tags}
          maintenance={maintenance}
          riskAssessments={riskAssessments}
          history={history}
          devices={linkedDevices}
          sensors={linkedSensors}
          documents={linkedDocuments}
          onNavigate={(path) => navigate(path)}
        />
      )}

      {/* ── Documents (DMS — single source of truth) ──────────────── */}
      {activeTab === "dms" && asset && (
        <DMSDocumentsPanel
          virtualSiteId={asset.virtual_site_id}
          entityType="asset"
          entityId={asset.id}
          defaultFolderPath={`Assets/${asset.asset_code} - ${asset.name}`}
          title="Documents linked to this asset"
        />
      )}

      {/* ── 3D Tags (unchanged) ───────────────────────────────────── */}
      {activeTab === "tags" && (
        <Card>
          <CardHeader>
            <CardTitle>3D Tags</CardTitle>
          </CardHeader>
          {tags.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-surface-500">
              <Tag className="h-10 w-10 mb-3 text-surface-600" />
              <p>No 3D tags assigned to this asset</p>
              <p className="mt-1 text-xs text-surface-600">
                Open a digital twin or point cloud, switch to the Tag tool, and click on the scene.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {tags.map((tag) => {
                const g = tag.geometry || {};
                const isPoint =
                  typeof g.x === "number" &&
                  typeof g.y === "number" &&
                  typeof g.z === "number";
                const sourceKind = tag.digital_twin_id ? "twin" : tag.point_cloud_id ? "cloud" : null;
                const sourceLink = tag.digital_twin_id
                  ? `/digital-twins/${tag.digital_twin_id}?tag=${tag.id}`
                  : tag.point_cloud_id
                    ? `/point-clouds/${tag.point_cloud_id}?tag=${tag.id}`
                    : null;
                return (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between rounded-lg border border-surface-700/50 bg-surface-800/50 p-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="flex h-8 w-8 items-center justify-center rounded-lg"
                        style={{
                          backgroundColor: tag.color
                            ? `${tag.color}26`
                            : "rgba(168, 85, 247, 0.15)",
                        }}
                      >
                        <Tag className="h-4 w-4" style={{ color: tag.color || "#a855f7" }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-surface-200 truncate">
                          {tag.label || "Untagged"}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-surface-500">
                          <span>{tag.tag_type}</span>
                          {sourceKind === "twin" && (
                            <span className="flex items-center gap-1 text-purple-400">
                              <Boxes className="h-3 w-3" /> Digital twin
                            </span>
                          )}
                          {sourceKind === "cloud" && (
                            <span className="flex items-center gap-1 text-blue-400">
                              <Cloud className="h-3 w-3" /> Point cloud
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        {isPoint && (
                          <p className="font-mono text-xs text-surface-400">
                            ({(g.x as number).toFixed(2)}, {(g.y as number).toFixed(2)}, {(g.z as number).toFixed(2)})
                          </p>
                        )}
                        <p className="text-xs text-surface-500">{formatDate(tag.created_at)}</p>
                      </div>
                      {sourceLink && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(sourceLink)}
                          title={
                            sourceKind === "twin"
                              ? "Open this tag in the 3D digital twin viewer"
                              : "Open this tag in the point cloud viewer"
                          }
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Open in 3D
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* ── Instrumentation (devices + sensors linked to this asset) ──── */}
      {activeTab === "instrumentation" && (
        <Card>
          <CardHeader>
            <CardTitle>Linked instrumentation</CardTitle>
          </CardHeader>
          <p className="mb-3 text-xs text-surface-400">
            Devices and sensors that monitor this asset. Manage device/sensor metadata, sources, and 3D placements on the{" "}
            <Link to="/instrumentation" className="text-brand-300 hover:text-brand-200">
              Instrumentation page
            </Link>
            .
          </p>
          {linkedDevices.length === 0 && linkedSensors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-surface-500">
              <Radio className="mb-3 h-10 w-10 text-surface-600" />
              <p className="text-sm">No devices or sensors are linked to this asset yet.</p>
              <p className="mt-1 text-xs">
                Open the Instrumentation page and pick this asset when creating or editing a device.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {linkedDevices.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-surface-400">
                    Devices ({linkedDevices.length})
                  </h4>
                  <div className="space-y-2">
                    {linkedDevices.map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center gap-3 rounded-lg border border-surface-700/50 bg-surface-900/40 px-3 py-2"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500/30 to-purple-500/30 ring-1 ring-white/10">
                          <Cpu className="h-4 w-4 text-brand-200" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-surface-100">
                            {d.name}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-surface-400">
                            <span className="rounded bg-surface-800 px-1.5 py-0.5 uppercase tracking-wider text-surface-300">
                              {d.source_type.replace(/_/g, " ")}
                            </span>
                            {d.device_type && <span>{d.device_type}</span>}
                            {d.manufacturer && <span>· {d.manufacturer}</span>}
                            <span>· {d.sensor_count} sensor{d.sensor_count === 1 ? "" : "s"}</span>
                          </div>
                        </div>
                        <Link
                          to={`/instrumentation/devices/${d.id}`}
                          className="inline-flex shrink-0 items-center gap-1 rounded bg-brand-500/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-200 hover:bg-brand-500/25 hover:text-brand-100"
                          title={`Open ${d.name} in the Instrumentation sidebar`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          Instrumentation
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {linkedSensors.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-surface-400">
                    Sensors ({linkedSensors.length})
                  </h4>
                  <div className="space-y-2">
                    {linkedSensors.map((s) => {
                      const data = s.data || {};
                      const value = (data as { value?: unknown }).value;
                      const ts = (data as { ts?: string }).ts;
                      const sensorTag = sensorTagBySensor.get(s.id);
                      // Deep-link target — point cloud takes priority over
                      // digital twin if (rarely) the same tag references both.
                      // Both viewers honor `?sensor_tag=<id>` and auto-focus
                      // the marker on first render.
                      const tagHref = sensorTag?.point_cloud_id
                        ? `/point-clouds/${sensorTag.point_cloud_id}?sensor_tag=${sensorTag.id}`
                        : sensorTag?.digital_twin_id
                          ? `/digital-twins/${sensorTag.digital_twin_id}?sensor_tag=${sensorTag.id}`
                          : null;
                      const tagLabel = sensorTag?.point_cloud_id
                        ? "View in Point Cloud"
                        : "View in Digital Twin";
                      return (
                        <div
                          key={s.id}
                          className="flex items-center gap-3 rounded-lg border border-surface-700/50 bg-surface-900/40 px-3 py-2"
                        >
                          <Radio className="h-4 w-4 shrink-0 text-cyan-300" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="truncate text-sm font-medium text-surface-100">
                                {s.name}
                              </span>
                              {s.sensor_type && (
                                <span className="rounded bg-surface-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-surface-300">
                                  {s.sensor_type}
                                </span>
                              )}
                              {s.source_channel && (
                                <span className="rounded bg-cyan-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-cyan-200">
                                  {s.source_channel}
                                </span>
                              )}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-3 text-[11px] text-surface-400">
                              <span className="font-mono text-brand-200">
                                {typeof value === "number"
                                  ? value.toFixed(2)
                                  : value !== undefined
                                    ? String(value)
                                    : "—"}
                                {s.unit && value !== undefined ? ` ${s.unit}` : ""}
                              </span>
                              {ts && <span className="text-surface-500">{formatDate(ts)}</span>}
                            </div>
                          </div>
                          {/* Two explicit action links: jump to the
                              Instrumentation sidebar, or — only if this sensor
                              is pinned in a 3D model — jump straight to the
                              viewer with the tag pre-focused. */}
                          {tagHref && (
                            <Link
                              to={tagHref}
                              className="inline-flex shrink-0 items-center gap-1 rounded bg-cyan-500/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-200 hover:bg-cyan-500/25 hover:text-cyan-100"
                              title={`${tagLabel} — ${sensorTag?.label || s.name}`}
                            >
                              <Tag className="h-3 w-3" />
                              3D Tag
                            </Link>
                          )}
                          <Link
                            to={`/instrumentation/sensors/${s.id}`}
                            className="inline-flex shrink-0 items-center gap-1 rounded bg-brand-500/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-200 hover:bg-brand-500/25 hover:text-brand-100"
                            title={`Open ${s.name} in the Instrumentation sidebar`}
                          >
                            <ExternalLink className="h-3 w-3" />
                            Instrumentation
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ── Maintenance (unchanged) ───────────────────────────────── */}
      {activeTab === "maintenance" && (
        <Card>
          <CardHeader>
            <CardTitle>Maintenance Tasks</CardTitle>
          </CardHeader>
          {maintenance.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-surface-500">
              <Wrench className="h-10 w-10 mb-3 text-surface-600" />
              <p>No maintenance tasks for this asset</p>
            </div>
          ) : (
            <div className="space-y-3">
              {maintenance.map((task) => (
                <a
                  key={task.id}
                  href={`#/maintenance?task=${task.id}`}
                  className="group flex items-center justify-between rounded-lg border border-surface-700/50 bg-surface-800/50 p-3 transition-colors hover:border-amber-500/40 hover:bg-amber-500/5"
                  title="Open this task in the Maintenance page"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15">
                      <Wrench className="h-4 w-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-surface-200 group-hover:text-amber-200">
                        {task.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {task.task_type && <Badge variant="default">{task.task_type}</Badge>}
                        {task.priority && (
                          <Badge
                            variant={
                              task.priority === "high"
                                ? "danger"
                                : task.priority === "medium"
                                  ? "warning"
                                  : "default"
                            }
                          >
                            {task.priority}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right flex flex-col items-end gap-1">
                      <StatusBadge status={task.status} />
                      {task.due_date && (
                        <div className="flex items-center gap-1 text-xs text-surface-500">
                          <Clock className="h-3 w-3" />
                          {formatDate(task.due_date)}
                        </div>
                      )}
                    </div>
                    <ExternalLink className="h-3.5 w-3.5 text-surface-500 group-hover:text-amber-200" />
                  </div>
                </a>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ── Risk (unchanged) ──────────────────────────────────────── */}
      {activeTab === "risk" && (
        <Card>
          <CardHeader>
            <CardTitle>Risk Assessments</CardTitle>
          </CardHeader>
          {riskAssessments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-surface-500">
              <ShieldAlert className="h-10 w-10 mb-3 text-surface-600" />
              <p>No risk assessments for this asset</p>
            </div>
          ) : (
            <div className="space-y-3">
              {riskAssessments.map((ra) => (
                <div
                  key={ra.id}
                  className="flex items-center justify-between rounded-lg border border-surface-700/50 bg-surface-800/50 p-3"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-lg text-lg font-bold ${
                        (ra.risk_score || 0) >= 15
                          ? "bg-red-500/15 text-red-400"
                          : (ra.risk_score || 0) >= 8
                            ? "bg-amber-500/15 text-amber-400"
                            : "bg-emerald-500/15 text-emerald-400"
                      }`}
                    >
                      {ra.risk_score ?? "-"}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-surface-200">
                        Risk Score: {ra.risk_score ?? "N/A"}
                      </p>
                      <p className="text-xs text-surface-500">
                        L:{ra.likelihood ?? "-"} x C:{ra.consequence ?? "-"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <StatusBadge status={ra.status} />
                    <p className="text-xs text-surface-500">{formatDate(ra.assessed_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ── History ───────────────────────────────────────────────── */}
      {activeTab === "history" && (
        <Card>
          <CardHeader>
            <CardTitle>
              <span className="flex items-center gap-2">
                <History className="h-4 w-4 text-brand-400" />
                Change History
              </span>
            </CardTitle>
          </CardHeader>
          <div className="flex flex-wrap gap-4 mb-4 pb-3 border-b border-surface-700/50">
            <div>
              <span className="text-xs text-surface-500">Created:</span>{" "}
              <span className="text-sm text-surface-200">{formatDateTime(asset.created_at)}</span>
            </div>
            <div>
              <span className="text-xs text-surface-500">Last updated:</span>{" "}
              <span className="text-sm text-surface-200">{formatDateTime(asset.updated_at)}</span>
            </div>
          </div>
          {history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-surface-500">
              <History className="h-10 w-10 mb-3 text-surface-600" />
              <p>No history recorded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-surface-700/50 bg-surface-800/40 p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          entry.action === "created"
                            ? "success"
                            : entry.action === "deleted"
                              ? "danger"
                              : "default"
                        }
                      >
                        {entry.action}
                      </Badge>
                      <span className="text-sm text-surface-200">
                        {entry.changed_by_name || entry.changed_by_email || "Unknown user"}
                      </span>
                      {entry.changed_by_email && entry.changed_by_name && (
                        <span className="text-xs text-surface-500">
                          ({entry.changed_by_email})
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-surface-500 whitespace-nowrap">
                      {formatDateTime(entry.changed_at)}
                    </span>
                  </div>
                  {entry.description && (
                    <p className="text-xs text-surface-400 mt-1.5">{entry.description}</p>
                  )}
                  {entry.changes && entry.action === "updated" && (
                    <div className="mt-2 space-y-1">
                      {Object.entries(entry.changes as Record<string, { old: unknown; new: unknown }>).map(
                        ([field, diff]) => (
                          <div
                            key={field}
                            className="flex items-start gap-2 text-xs"
                          >
                            <span className="text-surface-400 capitalize min-w-[8rem]">
                              {field.replace(/_/g, " ")}
                            </span>
                            <span className="text-red-300 line-through truncate max-w-xs">
                              {formatChangeValue(diff?.old)}
                            </span>
                            <span className="text-surface-500">→</span>
                            <span className="text-emerald-300 truncate max-w-xs">
                              {formatChangeValue(diff?.new)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ── Delete asset confirm ──────────────────────────────────── */}
      <Modal
        isOpen={showDeleteAsset}
        onClose={() => { setShowDeleteAsset(false); setDeleteAssetError(null); }}
        title="Delete Asset"
      >
        <div className="space-y-4">
          {deleteAssetError && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{deleteAssetError}</span>
            </div>
          )}
          <p className="text-sm text-surface-300">
            Delete <span className="font-medium text-surface-100">{asset.name}</span>
            {" "}(<span className="font-mono">{asset.asset_code}</span>)? This removes the
            asset and all of its 3D tags. Maintenance tasks, risk assessments, and
            documents attached to this asset will also be affected. This cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => { setShowDeleteAsset(false); setDeleteAssetError(null); }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteAsset} isLoading={isDeletingAsset}>
              <Trash2 className="h-4 w-4" />
              Delete Asset
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}


/** Label + control wrapper used by the inline editor on the Details tab. */
function FieldRow({
  label, required, children,
}: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-surface-300">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      {children}
    </div>
  );
}


/** Simple enum dropdown with an empty-option "Unset" sentinel. */
function SelectInput({
  value, onChange, options, disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-lg border border-surface-600 bg-surface-800/50 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
    >
      <option value="">— Unset —</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o.replace(/_/g, " ")}
        </option>
      ))}
    </select>
  );
}
